const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/orderActions.ts';
import moment from 'moment';
import { IKISAPIAction, IKISAPIMeta, kisapiActionTypes } from '../defs/actionTypes';
import { KISIM_TIME_FORMAT } from '../defs/generalDefs';
import { KISAPI as API } from '../middleware/kisapi';
import { Action } from 'redux';
import { apiLoadOrderRights, apiLoadOrders } from '@cistec/common_web/actions/kisapi/orders';

export const LOAD_ORDERS = kisapiActionTypes('LOAD_ORDERS');
export const LOAD_ORDER_RIGHTS = kisapiActionTypes('LOAD_ORDER_RIGHTS');

export const FORGET_ORDERS = 'FORGET_ORDERS';

export interface ILoadOrdersMeta extends IKISAPIMeta {
    patnr: string;
    kat?: string;
    fkeys?: (string | undefined)[];
    withHotspots: boolean;
    evenIfDeleted: boolean;
    addApplicationManually?: boolean;
    rType: string;
    mindat: string;
    maxdat: string;
    callerId?: string;
}

export interface ILoadOrdersAction extends IKISAPIAction {
    meta: ILoadOrdersMeta;
}

export interface IForgetOrdersAction extends Action {
    patnr: string;
}

export interface ILoadOrderRightsMeta extends IKISAPIMeta {
    sender: string;
    ordernr: string;
}

export interface ILoadOrderRightsAction extends IKISAPIAction {
    meta: ILoadOrderRightsMeta;
}

/**
 * Action to load a given patient's orders overlapping the given time window.
 * Optionally, the order's hotspots are loaded as well.
 *
 * If both mindat and maxdat are omitted, the endpoint will load orders with
 * STARTDAT < now <= STOPDAT.
 *
 * @param patnr
 * @param mindat Orders are included if mindat <= STOPDAT of the order.
 *    If mindat is omitted, the current time will be used as the default value.
 * @param maxdat Orders are included if maxdat > STARTDAT of the order.
 *    If maxdat is omitted, the current time will be used as the default value.
 * @param kat must be used in combination with fkeys; only orders matching KAT/FKEYs are loaded
 * @param fkeys see above
 * @param stat1 Option to search for a certain stat1
 * @param kind Option to search for a certain kind
 * @param typ
 * @param withHotspots Also load hotspots within the given time window.
 * @param withRights Include the rights the user has for this order in the answer
 * @param evenIfDeleted Put records with STATUS==='I' into the app state.
 * @param larsOrders true=look in LarsOrders; false=look in Orders; null=auto
 * @param callerId Optional metadata used to identify the calling instance.
 * @param showDlgWhenResultHasMoreRows Option to show a dialog window and notify user that the result is bigger than one
 * @param onlyMostRecent Option to only fetch the most recent order within the time range given
 * @return: An action object.
 */
//TODO KLY: make own for patapp --> create own patapi path with static params and patnr from session
export function actionLoadOrders(
    patnr: string,
    mindat?: string,
    maxdat?: string,
    kat?: string,
    fkeys?: (string | undefined)[] | undefined,
    stat1: string | undefined = undefined,
    kind: string | undefined = undefined,
    typ: string | undefined = undefined,
    withHotspots: boolean = false,
    withRights: boolean = false,
    evenIfDeleted: boolean = false,
    larsOrders: boolean | null = null,
    callerId?: string,
    showDlgWhenResultHasMoreRows = true,
    onlyMostRecent?: boolean
) {
    const now = moment().format(KISIM_TIME_FORMAT);
    mindat = mindat ? mindat : now;
    maxdat = maxdat ? maxdat : now;

    let manualAppl: boolean = false;
    let rType: 'Orders' | 'LarsOrders' = 'Orders';

    if (typeof larsOrders === 'boolean') {
        if (larsOrders) {
            rType = 'LarsOrders';
        }
    } else {
        // FIXME Remove this case, as a KAT of ORDLAB does not necessarily mean it's a LarsOrder!
        if (kat === 'ORDLAB') {
            rType = 'LarsOrders';
            // Also, applications have to be added manually (=STARTDAT)
            manualAppl = true;
        }
    }

    // KIS API does not return all orders but orders with FKEY==='' when loaded with the wildcard '%'. Workaround:
    // Replace wildcard with an empty string if all FKEYs should be loaded.
    const fkeysToLoad = fkeys && fkeys.length === 1 && fkeys[0] === '%' ? [] : fkeys;

    const meta: ILoadOrdersMeta = {
        patnr,
        kat,
        fkeys: fkeysToLoad,
        withHotspots,
        evenIfDeleted,
        addApplicationManually: manualAppl,
        rType,
        mindat,
        maxdat,
        callerId,
        showDlgWhenResultHasMoreRows: showDlgWhenResultHasMoreRows,
    };

    return {
        type: LOAD_ORDERS.combined,
        [API]: apiLoadOrders(
            patnr,
            undefined,
            mindat,
            maxdat,
            kat,
            fkeysToLoad,
            kind,
            typ,
            stat1,
            withHotspots,
            withRights,
            rType,
            undefined,
            undefined,
            undefined,
            onlyMostRecent
        ),
        meta,
    };
}

/**
 * Causes the store to forget about all the orders of a patient without
 * actually deleting anything. Call this to free up some memory.
 */
export function actionForgetOrders(patnr: string): IForgetOrdersAction {
    return {
        type: FORGET_ORDERS,
        patnr,
    };
}

/**
 * Action to load the rights specific to a given order.
 */
export function loadOrderRights(sender: string, ordernr: string) {
    const meta: ILoadOrderRightsMeta = {
        sender,
        ordernr,
        suppressErrorMsg: true,
    };

    return {
        type: LOAD_ORDER_RIGHTS.combined,
        [API]: apiLoadOrderRights(sender, ordernr),
        meta,
    };
}
